import { useCheckEmail, useRequestLoginCode, useLoginWithCode } from '../../api/registration/hooks';
import { useLinkFamilyDevice } from '../../api/guardian/family/hooks';
import { RegistrationApiError } from '../../api/registration/errors';
import { useResendCountdown } from '../../hooks/use-resend-countdown';
/**
 * Landing Page — THE unified sign-in of the portal.
 * Shown on unlinked browsers only (linked family devices go straight to the
 * profile picker via app.tsx). Passwordless flow in three steps: entry
 * buttons → email form (sends a 6-digit access code) → code entry, serving
 * guardians and students with an email alike. Staff (admin/moderator) keep
 * their password logins at ?mod=admin / ?mod=moderator.
 */

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  hasFamilyDeviceToken,
  setFamilyDeviceToken,
} from '../../services/family-device-storage';
import { authService } from '../../services/authentication/auth-service';
import { TurnstileWidget } from '../../components/turnstile-widget';
import { isCaptchaEnabled } from '../../components/captcha';
import type { TurnstileHandle } from '../../components/turnstile-widget';
import { GuardianSignupWizard } from '../signup/guardian-signup-wizard';
import { LanguageSelect } from '../../components/language-select';
import { VersionFooter } from './version-footer';
import { FormField, FormMessage, Input } from '../../components/form-field';
import { Button } from '../../components/button';
import auth from './auth.module.css';
import landing from './landing-page.module.css';

type LandingStep = 'buttons' | 'email' | 'code' | 'signup';

export function LandingPage() {
  const checkEmail = useCheckEmail();
  const requestLoginCode = useRequestLoginCode();
  const loginWithCode = useLoginWithCode();
  const { mutateAsync: linkDevice } = useLinkFamilyDevice();
  const { t, i18n } = useTranslation();

  const [step, setStep] = useState<LandingStep>('buttons');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // True when the typed email has no account — we suggest signing up instead
  const [emailNotFound, setEmailNotFound] = useState(false);

  // Captcha (only enforced when a Turnstile sitekey is configured)
  const captchaEnabled = isCaptchaEnabled();
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaRef = useRef<TurnstileHandle>(null);

  // Resend cooldown countdown (seconds remaining)
  const [resendAt, setResendAt] = useState<Date | null>(null);
  const resendIn = useResendCountdown(resendAt);

  /** Step 2 → 3: emails the 6-digit access code (unknown emails → suggest signup) */
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setEmailNotFound(false);
    try {
      // No account with this email → don't pretend a code was sent
      const { exists } = await checkEmail(email);
      if (!exists) {
        setEmailNotFound(true);
        return;
      }

      const result = await requestLoginCode(
        email,
        i18n.language,
        captchaToken ?? undefined,
      );
      // Turnstile tokens are single-use — refresh for resend/retry
      captchaRef.current?.reset();
      setResendAt(new Date(result.resendAvailableAt));
      setCode('');
      setStep('code');
    } catch {
      setError(t('linkLogin.error'));
      captchaRef.current?.reset();
    } finally {
      setBusy(false);
    }
  };

  const handleResendCode = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await requestLoginCode(
        email,
        i18n.language,
        captchaToken ?? undefined,
      );
      setResendAt(new Date(result.resendAvailableAt));
    } catch (err) {
      if (err instanceof RegistrationApiError && err.status === 429) {
        setError(t('signup.errors.tooManyCodes'));
      } else {
        setError(t('linkLogin.error'));
      }
    } finally {
      captchaRef.current?.reset();
      setBusy(false);
    }
  };

  /** Step 3: validates the code → session; guardians also link this device */
  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const response = await loginWithCode(email, code);
      authService.adoptLoginResponse(response);

      // ONLY a guardian login activates the family on this browser (links it
      // as a family device). A student's email login is personal: no link.
      if (response.user.userType === 'GUARDIAN') {
        try {
          if (!hasFamilyDeviceToken()) {
            const deviceName = t('guardian.devices.autoName', {
              name: response.user.firstName || response.user.name || 'Guardian',
            });
            const result = await linkDevice(deviceName);
            setFamilyDeviceToken(result.deviceToken);
          }
        } catch {
          // Non-fatal — the guardian can link the device from the dashboard
        }
      }

      window.location.href = window.location.pathname;
    } catch {
      setError(t('loginCode.invalid'));
      setCode('');
      setBusy(false);
    }
  };

  const backToStart = () => {
    setStep('buttons');
    setError(null);
    setCode('');
  };

  // Account creation is part of the landing too — the wizard renders in place
  // (its own full-screen card; its "back to home" reloads into this landing)
  if (step === 'signup') {
    return <GuardianSignupWizard />;
  }

  return (
    // student-portal + family-auth: the student design system, restyled by the
    // `:global(.family-auth)` variants beside each rule (auth.module.css and its kin)
    <div className={`${auth.loginContainer} portal-shell student-portal family-auth`}>

      <div className={auth.loginContent}>
        <div className={`${auth.loginCard} framed`}>
          <div className={auth.loginHeader}>
            <h1 className={auth.loginTitle}>{t('landing.title')}</h1>
            <p className={auth.loginSubtitle}>{t('landing.subtitle')}</p>
          </div>

          <div className={auth.loginLanguageSelector}>
            <LanguageSelect compact />
          </div>

          {step === 'buttons' && (
            <div className={landing.landingSections}>
              <section className={landing.landingSection}>
                <p className={landing.landingSection__hint}>{t('landing.hint')}</p>
                <div className={landing.landingSection__actions}>
                  <Button variant="primary" size="lg" block
                    type="button"
                    onClick={() => setStep('email')}
                  >
                    {t('landing.emailLogin')}
                  </Button>
                  <Button variant="gold" size="lg" block
                    type="button"
                    onClick={() => setStep('signup')}
                  >
                    {t('landing.createAccount')}
                  </Button>
                </div>
              </section>
            </div>
          )}

          {step === 'email' && (
            <form onSubmit={(e) => void handleEmailSubmit(e)} className={auth.loginForm}>
              <p className={landing.landingSection__hint}>{t('landing.hint')}</p>
              <FormField label={t('login.email')} required>
                {(field) => (
                  <Input {...field}
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setEmailNotFound(false);
                    }}
                    placeholder={t('login.enterEmail')}
                    required
                    disabled={busy}
                    autoComplete="email"
                  />
                )}
              </FormField>
              {emailNotFound && (
                <div className={auth.signupExistsBox}>
                  {t('loginCode.emailNotFound')}{' '}
                  <button
                    type="button"
                    className={`${auth.loginSignupLink} ${landing.landingBackLink}`}
                    onClick={() => setStep('signup')}
                  >
                    {t('landing.createAccount')}
                  </button>
                </div>
              )}
              <TurnstileWidget ref={captchaRef} onToken={setCaptchaToken} />
              {error && <FormMessage>{error}</FormMessage>}
              <Button size="lg" block
                type="submit"
                disabled={!email || busy || (captchaEnabled && !captchaToken)}
              >
                {busy ? t('common.loading') : t('loginCode.send')}
              </Button>
            </form>
          )}

          {step === 'code' && (
            <form onSubmit={(e) => void handleCodeSubmit(e)} className={auth.loginForm}>
              <p className={landing.landingSection__hint}>{t('loginCode.hint', { email })}</p>
              <FormField label={t('signup.codeLabel')} required>
                {(field) => (
                  <Input {...field}
                    type="text"
                    inputMode="numeric"
                    pattern="\d{6}"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} className={auth.signupCodeInput}
                    placeholder="000000"
                    required
                    disabled={busy}
                    autoComplete="one-time-code"
                  />
                )}
              </FormField>
              <TurnstileWidget ref={captchaRef} onToken={setCaptchaToken} />
              {error && <FormMessage>{error}</FormMessage>}
              <Button size="lg" block
                type="submit"
                disabled={code.length !== 6 || busy}
              >
                {busy ? t('common.loading') : t('loginCode.submit')}
              </Button>
              <button
                type="button"
                className={auth.signupResend}
                onClick={() => void handleResendCode()}
                disabled={busy || resendIn > 0 || (captchaEnabled && !captchaToken)}
              >
                {resendIn > 0
                  ? t('signup.resendIn', { seconds: resendIn })
                  : t('signup.resendCode')}
              </button>
            </form>
          )}

          {step !== 'buttons' && (
            <p className={auth.loginSignupHint}>
              <button
                type="button"
                className={`${auth.loginSignupLink} ${landing.landingBackLink}`}
                onClick={backToStart}
              >
                {t('signup.backToHome')}
              </button>
            </p>
          )}
        </div>

        <div className={auth.loginVersionBox}>
          <VersionFooter />
        </div>
      </div>
    </div>
  );
}
